/**
 * @fileoverview User menu dropdown component.
 * Shows user display name and provides logout functionality.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * UserMenu renders a user avatar button that expands to show
 * user details and a logout action.
 *
 * @returns {JSX.Element|null} The user menu, or null if not authenticated
 */
function UserMenu() {
  const { authenticated, user, activeOrg, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!authenticated || !user) {
    return null;
  }

  const initials = (user.display_name || user.email || '?')
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="user-menu" ref={menuRef} style={{ position: 'relative' }}>
      <button
        className="user-menu__trigger"
        onClick={() => setOpen(!open)}
        aria-label="User menu"
        aria-expanded={open}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: '2px solid #3182ce',
          backgroundColor: '#ebf4ff',
          color: '#3182ce',
          fontWeight: 'bold',
          fontSize: '0.875rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {initials}
      </button>

      {open && (
        <div
          className="user-menu__dropdown"
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: '0.5rem',
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minWidth: '220px',
            zIndex: 1000,
            padding: '0.5rem 0',
          }}
        >
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{user.display_name}</div>
            <div style={{ color: '#666', fontSize: '0.8rem' }}>{user.email}</div>
            {activeOrg && (
              <div style={{ color: '#888', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {activeOrg.name} ({activeOrg.role})
              </div>
            )}
          </div>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.5rem 1rem',
              textAlign: 'left',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: '#e53e3e',
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
